/**
 * @fileoverview Entity Service - Firebase CRUD operations for Entity management
 * Handles all entity-related database operations with proper error handling and user scoping
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { 
  createEntity, 
  updateEntity, 
  validateEntity,
  ENTITY_TYPES,
  ENTITY_STATUS
} from '../models/Entity';

/**
 * Error class for entity service operations
 */
export class EntityServiceError extends Error {
  constructor(message, code = 'ENTITY_SERVICE_ERROR') {
    super(message);
    this.name = 'EntityServiceError';
    this.code = code;
  }
}

/**
 * Gets the entities collection reference for a specific user
 * @param {string} userId - User ID
 * @returns {import('firebase/firestore').CollectionReference} Entities collection reference
 */
function getEntitiesCollection(userId) {
  if (!userId) {
    throw new EntityServiceError('User ID is required', 'MISSING_USER_ID');
  }
  return collection(db, 'users', userId, 'entities');
}

/**
 * Creates a new entity
 * @param {import('../models/Entity').EntityFormData} entityData - Entity form data
 * @param {string} userId - User ID who owns the entity
 * @returns {Promise<{id: string, ...import('../models/Entity').Entity}>} Created entity with ID
 */
export async function createEntityService(entityData, userId) {
  try {
    // Validate input data
    const validation = validateEntity(entityData);
    if (!validation.isValid) {
      throw new EntityServiceError(
        `Validation failed: ${validation.errors.join(', ')}`,
        'VALIDATION_ERROR'
      );
    }

    // Create entity object with server timestamp
    const entityObject = createEntity(entityData, userId);
    const entityWithTimestamp = {
      ...entityObject,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add to Firestore
    const entitiesRef = getEntitiesCollection(userId);
    const docRef = await addDoc(entitiesRef, entityWithTimestamp);

    // Return entity with generated ID
    return {
      id: docRef.id,
      ...entityObject,
      createdAt: new Date(), // Use local date for immediate use
      updatedAt: new Date()
    };

  } catch (error) {
    if (error instanceof EntityServiceError) {
      throw error;
    }
    console.error('Error creating entity:', error);
    throw new EntityServiceError(
      'Failed to create entity. Please try again.',
      'CREATE_FAILED'
    );
  }
}

/**
 * Gets all entities for a user
 * @param {string} userId - User ID
 * @returns {Promise<import('../models/Entity').Entity[]>} Array of user entities
 */
export async function getUserEntities(userId) {
  try {
    const entitiesRef = getEntitiesCollection(userId);
    // Temporary: Remove orderBy to avoid index requirement
    const q = query(
      entitiesRef,
      where('ownerId', '==', userId)
      // orderBy('createdAt', 'desc') // Will add back after index is created
    );

    const querySnapshot = await getDocs(q);
    const entities = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      entities.push({
        id: doc.id,
        ...data,
        // Convert Firestore timestamps to Date objects
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      });
    });

    // Sort locally by createdAt (newest first)
    entities.sort((a, b) => b.createdAt - a.createdAt);

    return entities;

  } catch (error) {
    console.error('Error fetching user entities:', error);
    throw new EntityServiceError(
      'Failed to load entities. Please refresh the page.',
      'FETCH_FAILED'
    );
  }
}

/**
 * Gets a specific entity by ID
 * @param {string} entityId - Entity ID
 * @param {string} userId - User ID (for access control)
 * @returns {Promise<import('../models/Entity').Entity|null>} Entity data or null if not found
 */
export async function getEntityById(entityId, userId) {
  try {
    const entitiesRef = getEntitiesCollection(userId);
    const entityDoc = doc(entitiesRef, entityId);
    const docSnap = await getDoc(entityDoc);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    
    // Verify ownership
    if (data.ownerId !== userId) {
      throw new EntityServiceError(
        'Access denied. You do not own this entity.',
        'ACCESS_DENIED'
      );
    }

    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };

  } catch (error) {
    if (error instanceof EntityServiceError) {
      throw error;
    }
    console.error('Error fetching entity:', error);
    throw new EntityServiceError(
      'Failed to load entity details.',
      'FETCH_ENTITY_FAILED'
    );
  }
}

/**
 * Updates an existing entity
 * @param {string} entityId - Entity ID to update
 * @param {Partial<import('../models/Entity').EntityFormData>} updates - Fields to update
 * @param {string} userId - User ID (for access control)
 * @returns {Promise<import('../models/Entity').Entity>} Updated entity
 */
export async function updateEntityService(entityId, updates, userId) {
  try {
    // Get existing entity first
    const existingEntity = await getEntityById(entityId, userId);
    if (!existingEntity) {
      throw new EntityServiceError(
        'Entity not found or access denied.',
        'ENTITY_NOT_FOUND'
      );
    }

    // Validate updates
    const updatedData = { ...existingEntity, ...updates };
    const validation = validateEntity(updatedData);
    if (!validation.isValid) {
      throw new EntityServiceError(
        `Validation failed: ${validation.errors.join(', ')}`,
        'VALIDATION_ERROR'
      );
    }

    // Create updated entity object
    const updatedEntity = updateEntity(existingEntity, updates);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Update in Firestore
    const entitiesRef = getEntitiesCollection(userId);
    const entityDoc = doc(entitiesRef, entityId);
    await updateDoc(entityDoc, updateData);

    return {
      ...updatedEntity,
      updatedAt: new Date() // Use local date for immediate use
    };

  } catch (error) {
    if (error instanceof EntityServiceError) {
      throw error;
    }
    console.error('Error updating entity:', error);
    throw new EntityServiceError(
      'Failed to update entity. Please try again.',
      'UPDATE_FAILED'
    );
  }
}

/**
 * Deletes an entity (with validation)
 * @param {string} entityId - Entity ID to delete
 * @param {string} userId - User ID (for access control)
 * @returns {Promise<void>}
 */
export async function deleteEntityService(entityId, userId) {
  try {
    // Get existing entity first
    const existingEntity = await getEntityById(entityId, userId);
    if (!existingEntity) {
      throw new EntityServiceError(
        'Entity not found or access denied.',
        'ENTITY_NOT_FOUND'
      );
    }

    // Check if entity can be deleted
    const { canDelete, reason } = require('../models/Entity').canDeleteEntity(existingEntity);
    if (!canDelete) {
      throw new EntityServiceError(
        reason || 'This entity cannot be deleted.',
        'DELETE_NOT_ALLOWED'
      );
    }

    // Delete from Firestore
    const entitiesRef = getEntitiesCollection(userId);
    const entityDoc = doc(entitiesRef, entityId);
    await deleteDoc(entityDoc);

  } catch (error) {
    if (error instanceof EntityServiceError) {
      throw error;
    }
    console.error('Error deleting entity:', error);
    throw new EntityServiceError(
      'Failed to delete entity. Please try again.',
      'DELETE_FAILED'
    );
  }
}

/**
 * Gets active entities for a user (status = 'active')
 * @param {string} userId - User ID
 * @returns {Promise<import('../models/Entity').Entity[]>} Array of active entities
 */
export async function getActiveEntities(userId) {
  try {
    const entitiesRef = getEntitiesCollection(userId);
    const q = query(
      entitiesRef,
      where('ownerId', '==', userId),
      where('status', '==', ENTITY_STATUS.ACTIVE),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const entities = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      entities.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      });
    });

    return entities;

  } catch (error) {
    console.error('Error fetching active entities:', error);
    throw new EntityServiceError(
      'Failed to load active entities.',
      'FETCH_ACTIVE_FAILED'
    );
  }
}

/**
 * Ensures user has a default Personal entity, creates one if missing
 * @param {string} userId - User ID
 * @returns {Promise<import('../models/Entity').Entity>} Personal entity (existing or newly created)
 */
export async function ensurePersonalEntity(userId) {
  try {
    const entities = await getUserEntities(userId);
    
    // Look for existing personal entity
    const personalEntity = entities.find(entity => entity.type === ENTITY_TYPES.PERSONAL);
    
    if (personalEntity) {
      return personalEntity;
    }

    // Create default personal entity
    const personalEntityData = {
      name: 'Personal',
      type: ENTITY_TYPES.PERSONAL,
      currency: 'USD', // Default currency
      description: 'Personal financial accounts',
      status: ENTITY_STATUS.ACTIVE
    };

    const newPersonalEntity = await createEntityService(personalEntityData, userId);
    console.log('Created default personal entity for user:', userId);
    
    return newPersonalEntity;

  } catch (error) {
    console.error('Error ensuring personal entity:', error);
    throw new EntityServiceError(
      'Failed to initialize personal entity.',
      'PERSONAL_ENTITY_FAILED'
    );
  }
}

/**
 * Gets entities by type
 * @param {string} userId - User ID
 * @param {'personal'|'partnership'|'business'|'llc'} entityType - Entity type to filter by
 * @returns {Promise<import('../models/Entity').Entity[]>} Entities of specified type
 */
export async function getEntitiesByType(userId, entityType) {
  try {
    const entitiesRef = getEntitiesCollection(userId);
    const q = query(
      entitiesRef,
      where('ownerId', '==', userId),
      where('type', '==', entityType),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const entities = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      entities.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      });
    });

    return entities;

  } catch (error) {
    console.error('Error fetching entities by type:', error);
    throw new EntityServiceError(
      `Failed to load ${entityType} entities.`,
      'FETCH_BY_TYPE_FAILED'
    );
  }
}

/**
 * Gets entity count for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} Total number of entities
 */
export async function getEntityCount(userId) {
  try {
    const entities = await getUserEntities(userId);
    return entities.length;
  } catch (error) {
    console.error('Error getting entity count:', error);
    return 0;
  }
}

const entityServiceExports = {
  createEntityService,
  getUserEntities,
  getEntityById,
  updateEntityService,
  deleteEntityService,
  getActiveEntities,
  ensurePersonalEntity,
  getEntitiesByType,
  getEntityCount,
  EntityServiceError
};

export default entityServiceExports; 